<template>
  <div class="home detail-actions-page">
    <div class="px-8 py-4">
      <!-- Header -->
      <div class="invoice-details-header mb-4">
        <button type="button" class="btn invoice-back-button" :aria-label="$t('back')" @click="$router.go(-1)">
          <AppIcon name="bi-arrow-left" class="bi bi-arrow-left" aria-hidden="true" />
          <span>{{$t('back')}}</span>
        </button>
        <div class="invoice-title-row d-flex flex-column flex-lg-row justify-content-between align-items-start align-items-lg-center gap-3">
          <h3 class="invoice-reference mb-0">{{ delivery.reference }}</h3>
          <div class="invoice-actions" role="toolbar" :aria-label="$t('actions')">
            <button v-if="delivery.status != 'draft'" type="button" class="btn invoice-action-button invoice-action-button--primary" @click="ViewDelivery()">
              <AppIcon name="bi-eye" class="bi bi-eye" aria-hidden="true" />
              <span>{{$t('view')}}</span>
            </button>
            <button
              v-if="helper.checkAccess('edit_deliveries') != -1"
              type="button"
              class="btn invoice-action-button invoice-action-button--secondary"
              @click="editRow()"
            >
              <AppIcon name="bi-pencil" class="bi bi-pencil" aria-hidden="true" />
              <span>{{$t('edit')}}</span>
            </button>
            <div
              v-if="delivery.status != 'draft' && ((delivery.test && helper.checkAccess('create_deliveries') > 0) || (!delivery.test && (helper.checkAccess('create_invoices') > 0 || helper.checkAccess('create_returns') > 0)))"
              class="btn-group invoice-action-group"
            >
              <button
                id="deliveryTransferButton"
                class="btn invoice-action-button invoice-action-button--secondary dropdown-toggle"
                type="button"
                data-bs-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
              >
                <AppIcon name="bi-arrow-left-right" class="bi bi-arrow-left-right" aria-hidden="true" />
                <span>{{$t('transfert')}}</span>
              </button>
              <ul class="dropdown-menu dropdown-menu-end invoice-actions-menu" aria-labelledby="deliveryTransferButton">
                <li v-if="delivery.test">
                  <button type="button" class="dropdown-item" @click="confirmDelivery()">
                    <AppIcon name="bi-check-circle" class="bi bi-check-circle" aria-hidden="true" />
                    <span>{{$t('confirm')}}</span>
                  </button>
                </li>
                <li v-if="!delivery.test && helper.checkAccess('create_invoices') > 0">
                  <button type="button" class="dropdown-item" @click="transferInvoice()">
                    <AppIcon name="bi-receipt" class="bi bi-receipt" aria-hidden="true" />
                    <span>{{$t('invoice')}}</span>
                  </button>
                </li>
                <li v-if="!delivery.test && helper.checkAccess('create_returns') > 0">
                  <button type="button" class="dropdown-item" @click="transferReturn()">
                    <AppIcon name="bi-arrow-return-left" class="bi bi-arrow-return-left" aria-hidden="true" />
                    <span>{{$t('return')}}</span>
                  </button>
                </li>
              </ul>
            </div>
            <div v-if="delivery.status != 'draft' || helper.checkAccess('delete_deliveries') != -1" class="btn-group invoice-action-group">
              <button
                id="deliveryMoreActionsButton"
                class="btn invoice-action-button invoice-action-button--secondary invoice-action-button--icon dropdown-toggle"
                type="button"
                data-bs-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
                :aria-label="$t('more')"
                :title="$t('more')"
              >
                <AppIcon name="bi-three-dots" class="bi bi-three-dots" aria-hidden="true" />
              </button>
              <ul class="dropdown-menu dropdown-menu-end invoice-actions-menu" aria-labelledby="deliveryMoreActionsButton">
                <li v-if="delivery.status != 'draft'">
                  <button type="button" class="dropdown-item" @click="toggleReorder">
                    <AppIcon name="bi-arrow-down-up" class="bi bi-arrow-down-up" aria-hidden="true" />
                    <span>{{$t('reorder')}}</span>
                  </button>
                </li>
                <li v-if="delivery.status != 'draft' && helper.checkAccess('delete_deliveries') != -1"><hr class="dropdown-divider"></li>
                <li v-if="helper.checkAccess('delete_deliveries') != -1">
                  <button type="button" class="dropdown-item invoice-menu-action--danger" @click="deleteRow()">
                    <AppIcon name="bi-trash" class="bi bi-trash" aria-hidden="true" />
                    <span>{{$t('delete')}}</span>
                  </button>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center" style="padding:2rem">
        <div class="spinner-border" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      </div>

      <!-- Delivery Info -->
      <div v-else-if="delivery && delivery.id">
        <!-- Tabs -->
        <ul class="nav nav-tabs detail-page-tabs mb-4">
          <li class="nav-item">
            <a class="nav-link" :class="{active: step === 'overview'}" @click="step = 'overview'">{{$t('overview')}}</a>
          </li>
          <li class="nav-item" v-if="helper.checkAccess('view_attachments') != -1">
            <a class="nav-link" :class="{active: step === 'attachments'}" @click="step = 'attachments'">{{$t('attachments')}}</a>
          </li>
          <li class="nav-item">
            <a class="nav-link" :class="{active: step === 'history'}" @click="step = 'history'">{{$t('history')}}</a>
          </li>
        </ul>

        <!-- Tab Content -->
        <!-- Overview Tab -->
        <div v-if="step === 'overview'">
          <div class="row document-overview-grid">
            <!-- Left Column: Delivery Details -->
            <div class="col-md-8 document-overview-main">
              <div class="card mb-4 document-pdf-card">
                <div class="card-body p-0">
                  <invoice-template-modal v-if="delivery && delivery.id" inline :data="delivery" type="delivery" />
                </div>
              </div>
              <!-- Products Card -->
              <div v-if="reorderMode" class="card mb-4 document-products-card">
                <div class="card-header bg-white">
                  <div class="d-flex justify-content-between align-items-center">
                    <h6 class="mb-0"><AppIcon name="bi-box-seam" class="bi bi-box-seam me-2" />{{$t('products')}}</h6>
                    <div class="product-reorder-actions">
                        <div v-if="reorderMode" class="d-flex flex-wrap gap-2" role="group" :aria-label="$t('reorder')">
                            <button type="button" class="btn product-action-button product-action-button--save" @click="saveOrder">
                                <AppIcon name="bi-check-lg" class="bi bi-check-lg" aria-hidden="true" />
                                {{$t('save')}}
                            </button>
                            <button type="button" class="btn product-action-button" @click="toggleReorder">
                                <AppIcon name="bi-x-lg" class="bi bi-x-lg" aria-hidden="true" />
                                {{$t('cancel')}}
                            </button>
                        </div>
                    </div>
                  </div>
                </div>
                <div class="card-body table-responsive">
                  <table class="table mb-0 table-striped small">
                    <thead>
                      <tr class="text-secondary">
                        <th>{{$t('product_label')}}</th>
                        <th>{{$t('quantity')}}</th>
                        <th v-if="reorderMode" style="width: 100px;">{{$t('actions')}}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="(product, index) in delivery.products" :key="product.id">
                        <td><div class="product-rich-text" v-html="sanitizeRichText(product.product_label)"></div></td>
                        <td>{{product.quantity}}</td>
                        <td v-if="reorderMode">
                            <div class="d-flex gap-1">
                                <button
                                  type="button"
                                  class="btn product-move-button"
                                  :aria-label="$t('reorder') + ' ↑'"
                                  :title="$t('reorder') + ' ↑'"
                                  :disabled="index === 0"
                                  @click="moveUp(index)"
                                >
                                  <AppIcon name="bi-arrow-up" class="bi bi-arrow-up" aria-hidden="true" />
                                </button>
                                <button
                                  type="button"
                                  class="btn product-move-button"
                                  :aria-label="$t('reorder') + ' ↓'"
                                  :title="$t('reorder') + ' ↓'"
                                  :disabled="index === delivery.products.length - 1"
                                  @click="moveDown(index)"
                                >
                                  <AppIcon name="bi-arrow-down" class="bi bi-arrow-down" aria-hidden="true" />
                                </button>
                            </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Notes Card -->
              <div v-if="delivery.notes" class="card document-notes-card">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-sticky" class="bi bi-sticky me-2" />{{$t('notes')}}</h6>
                </div>
                <div class="card-body">
                  <p class="mb-0">{{ delivery.notes }}</p>
                </div>
              </div>
            </div>

            <!-- Right Column: Client Info -->
            <div class="col-md-4 document-overview-sidebar">
              <div class="card mb-4 document-info-card">
                <div class="card-header bg-white"><h6 class="mb-0"><AppIcon name="bi-truck" class="bi bi-truck me-2" />{{$t('delivery')}} {{$t('information')}}</h6></div>
                <div class="card-body"><div class="row">
                  <div class="col-6 mb-3"><small class="text-muted d-block">{{$t('reference')}}</small><strong>{{delivery.reference}}</strong></div>
                  <div class="col-6 mb-3"><small class="text-muted d-block">{{$t('status')}}</small><span class="badge" :class="{'bg-secondary': delivery.status === 'pending', 'bg-success': delivery.status === 'delivered'}">{{ $t(delivery.status) }}</span></div>
                  <div class="col-6 mb-3"><small class="text-muted d-block">{{$t('order_date')}}</small><strong>{{formatDate(delivery.date)}}</strong></div>
                  <div class="col-6 mb-3"><small class="text-muted d-block">{{$t('created_by')}}</small><strong>{{delivery.user ? delivery.user.name : '-'}}</strong></div>
                  <div class="col-6 mb-3"><small class="text-muted d-block">{{$t('created_at')}}</small><strong>{{formatDate(delivery.created_at)}}</strong></div>
                  <div class="col-6 mb-3"><small class="text-muted d-block">{{$t('updated_at')}}</small><strong>{{formatDate(delivery.updated_at)}}</strong></div>
                </div></div>
              </div>
              <div class="card">
                <div class="card-header bg-white">
                  <h6 class="mb-0"><AppIcon name="bi-person" class="bi bi-person me-2" />{{$t('client')}}</h6>
                </div>
                <div class="card-body">
                  <div v-if="!delivery.client" class="text-muted">{{$t('no_client_found')}}</div>
                  <div v-else>
                    <div class="mb-3">
                      <small class="text-muted d-block">{{$t('reference')}}</small>
                      <strong>{{delivery?.client?.reference}}</strong>
                    </div>
                    <div class="mb-3">
                      <small class="text-muted d-block">{{$t('name')}}</small>
                      <strong>{{delivery?.client?.name}}</strong>
                    </div>
                    <div class="mb-3">
                      <small class="text-muted d-block">{{$t('phone')}}</small>
                      <strong>{{delivery?.client?.phone || '-'}}</strong>
                    </div>
                    <div>
                      <small class="text-muted d-block">{{$t('email')}}</small>
                      <strong>{{delivery?.client?.email || '-'}}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Other Tabs -->
        <div v-if="step === 'attachments' && helper.checkAccess('view_attachments') != -1">
          <attachments v-if="delivery && delivery.id" :entity_id="delivery.id" entity_type="delivery" />
        </div>

        <div v-if="step === 'history'">
          <document-history
            :history="history"
            :loading="loadingHistory"
            :pagination="historyPagination"
            @pageChange="changeHistoryPage"
          />
        </div>
      </div>
    </div>


            <!-- Edit Modal -->
            <add-edit-delivery-modal 
                v-if="editModalisOpen" 
                @modalClosed="editModalisOpen = false" 
                modalId="edit-delivery-modal-01" 
                :isModalOpen="editModalisOpen"  
                :data="delivery" 
                action="update" 
            ></add-edit-delivery-modal>
            <!-- Edit modal -->

            <!-- delivery modal -->
            <invoice-template-modal 
                v-if="invoiceModalisOpen" 
                @modalClosed="invoiceModalisOpen = false" 
                modalId="invoice-delivery-modal-01" 
                :isModalOpen="invoiceModalisOpen"  
                :data="delivery" 
                type="delivery">
            </invoice-template-modal>
            <!-- delivery modal -->

            <!-- create Modal -->
            <add-edit-invoice-modal 
                v-if="createInvoiceModalState" 
                @modalClosed="createInvoiceModalState = false"
                modalId="create-invoice-modal-01" 
                :isModalOpen="createInvoiceModalState" 
                :data="delivery" 
                action="transfer"
            ></add-edit-invoice-modal>
            <!-- create modal -->

            <!-- create Modal -->
            <add-edit-return-modal 
                v-if="createReturnModalState" 
                @modalClosed="createReturnModalState = false"
                modalId="create-return-modal-01" 
                :isModalOpen="createReturnModalState" 
                :data="delivery" 
                action="transfer"
            ></add-edit-return-modal>
            <!-- create modal -->

            <!-- create Modal -->
            <add-edit-credit-invoice-modal 
                v-if="createCreditInvoiceModalState" 
                @modalClosed="createCreditInvoiceModalState = false"
                modalId="create-return-modal-01" 
                :isModalOpen="createCreditInvoiceModalState" 
                :data="delivery" 
                action="transfer"
            ></add-edit-credit-invoice-modal>
            <!-- create modal -->


    </div>

</template>

<script>
// @ is an alias to /src
import helper from '../../helper'
import moment from 'moment'

import AddEditDeliveryModal from '../../components/delivery/AddEditDeliveryModal'
import AddEditInvoiceModal from '../../components/invoice/AddEditInvoiceModal'
import AddEditReturnModal from '../../components/return/AddEditReturnModal'
import AddEditCreditInvoiceModal from '../../components/creditInvoice/AddEditInvoiceModal'

import InvoiceTemplateModal from '../../components/invoiceLayout/InvoiceTemplateModal';

import attachments from '../../components/Attachment.vue'
import DocumentHistory from '../../components/DocumentHistory.vue'

export default {
  name: 'DeliveryDetails',
  components :{
    attachments,
    AddEditDeliveryModal,
    AddEditInvoiceModal,
    AddEditReturnModal,
    AddEditCreditInvoiceModal,
    InvoiceTemplateModal,
    DocumentHistory
  },
  data(){
    return {
        loading : false,
        loadingHistory : false,
        delivery : {},
        history : [],
        historyPagination : {
            count: 0,
            per_page: 10,
            current_page: 1,
            total_pages: 1
        },
        amount_paid : 0,
        total_purchases : 0,
        step : 'overview',
        total_quote : 0,
        invoiceModalisOpen : false,
        invoiceModal : null,
        editModal : null,
        editModalisOpen : false,
        createClientState : false,
        createInvoiceModalState : false,
        createReturnModalState : false,
        createCreditInvoiceModalState : false,
        reorderMode: false,
        originalProductsOrder: null
    }
  }, 
  created(){
    if(!this.$route.params || !this.$route.params.id) return this.$route.push('/deliveries')
    this.getDelivery(this.$route.params.id)
  },
  mounted(){
    window.mixpanel.track("delivery_details");

    //check permissions
    if(helper.checkAccess('view_deliveries') == -1) return this.$router.push({path : '/'})
    if(helper.checkAccess('view_deliveries') == -2) return this.$router.push({path : '/subscribe'})

  },
  methods : {
        sanitizeRichText(value){
            return helper.sanitizeRichText(value)
        },
         ViewDelivery(){
            this.invoiceData = this.delivery 
            this.invoiceModalisOpen = true;
        },
        async sharableLink() {
            const response = await helper.customPost(`${process.env.VUE_APP_API}/external-documents`, {
                document_id: this.delivery.id,
                document_type: 'delivery'
            });

            const link = response.data.link;

            try {
                await navigator.clipboard.writeText(link); // Copy to clipboard
                this.$toast.open({
                    message: this.$t('Copied'),
                    type: 'success',
                    position: 'bottom'
                });
            } catch (err) {
                this.$toast.open({
                    message: err.message,
                    type: 'error',
                    position: 'bottom'
                });
            }
        },
        async getDelivery(id){
            try{
                this.loading = true
                const response = await helper.customGet(`${process.env.VUE_APP_API}/deliveries/${id}`)
                this.delivery = response.data
                this.loading = false
                
            }catch(error){
                this.$router.push({path : '/deliveries'})
                this.loading = false
            }
        },
        async getDeliveryHistory(id){
            try{
                this.loadingHistory = true
                const response = await helper.customGet(`${process.env.VUE_APP_API}/deliveries/${id}/history`)
                this.history = response.data.data || response.data
                this.historyPagination = {
                    count: response.data.count || 0,
                    per_page: response.data.per_page || 10,
                    current_page: response.data.current_page || 1,
                    total_pages: response.data.total_pages || 1
                }
                this.loadingHistory = false
            }catch(error){
                alert('Error fetching history:', error)
                this.loadingHistory = false
            }
        },
        async changeHistoryPage(page){
            if(page < 1 || page > this.historyPagination.total_pages) return;
            
            try{
                this.loadingHistory = true
                const response = await helper.customGet(`${process.env.VUE_APP_API}/deliveries/${this.delivery.id}/history?page=${page}&per_page=${this.historyPagination.per_page}`)
                this.history = response.data.data || response.data
                this.historyPagination = {
                    count: response.data.count || 0,
                    per_page: response.data.per_page || 10,
                    current_page: response.data.current_page || 1,
                    total_pages: response.data.total_pages || 1
                }
                this.loadingHistory = false
            }catch(error){
                alert('Error fetching history page:', error)
                this.loadingHistory = false
            }
        },
        editRow(){
            // this.clearForm()
            this.editModalisOpen = true
            this.delivery.date = moment(this.delivery.date).format('YYYY-MM-DD')
        },
        async transferCreditInvoice(){
    
            this.delivery.total = this.delivery.products && this.delivery.products.length ? this.delivery.products.map(o => o.total).reduce((a, c) => { return a + c }) : 0;

            this.createCreditInvoiceModalState = !this.createCreditInvoiceModalState

        },
        async transferReturn(){

            this.delivery.total = this.delivery.products && this.delivery.products.length ? this.delivery.products.map(o => o.total).reduce((a, c) => { return a + c }) : 0;

            this.createReturnModalState = !this.createReturnModalState

        },
        async transferInvoice(){

            this.delivery.total = this.delivery.products && this.delivery.products.length ? this.delivery.products.map(o => o.total).reduce((a, c) => { return a + c }) : 0;
            this.createInvoiceModalState = !this.createInvoiceModalState

        },
        async confirmDelivery(){
            this.$swal.fire({
            title: `${this.$t('are_you_sure')}?`,
            text : this.$t('you_cant_revert'),
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: this.$t('yes'),
            showLoaderOnConfirm: true,
            preConfirm: async () => {

                try{

                const deliveryObject = {
                        id : null,
                        date : this.delivery.date,
                        notes : this.delivery.notes,
                        products : this.delivery.products,
                        client_id : this.delivery.client_id,
                        status : this.delivery.status,
                        total : this.delivery.total
                    }

                    if(this.delivery.reference){
                        deliveryObject.reference = this.delivery.reference.replace('TEST+++','')
                    }

                    const response = await helper.customPost(`${process.env.VUE_APP_API}/deliveries`,{
                        ...deliveryObject, 
                        transaction : {
                            amount : 0
                        }, 
                        products : deliveryObject.products},
                        {
                            showToast:false
                        })

                    this.$router.push({path : `/deliveries/${response.data.id}`})

                }catch(error){
                    this.$swal.showValidationMessage(`Request failed: ${error}`)
                }

            },

            }).then(async (result) => {
                if (result.isConfirmed) {

                    window.vueOpener.open({
                        message: `Livraison créée avec succès`,
                        type: 'success',
                        position : 'bottom'
                    });

                }
            })
        },
        clearForm(){
            this.delivery = {
                id : null,
                reference : '',
                client_id : null,
                client : {},
                date : '',
                total : 0,
                products : []
            }
        },
        async deleteRow(){
            this.$swal.fire({
            title: `${this.$t('are_you_sure')}?`,
            text : this.$t('you_cant_revert'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: this.$t('yes_delete_it')
            }).then(async (result) => {
                if (result.isConfirmed) {
                    await helper.customDelete(`${process.env.VUE_APP_API}/deliveries/${this.delivery.id}`)
                    this.$router.push({path : '/deliveries'})
                }
            })
        },
        
        createInvoiceModal(data){
            this.createInvoiceModalState = data
        },
        createReturnModal(data){
            this.createReturnModalState = data
        },
        createCreditInvoiceModal(data){
            this.createCreditInvoiceModalState = data
        },
        formatReference(number,id) { return helper.formatReference(number,id);},
        formatDate(date) { return helper.formatDate(date) },
        formatMoney(number) { return helper.formatMoney(number);},
        toggleReorder() {
            if (!this.reorderMode) {
                // Enter reorder mode: save current order to restore if cancelled
                this.originalProductsOrder = JSON.parse(JSON.stringify(this.delivery.products));
                this.reorderMode = true;
            } else {
                // Cancel reorder mode: restore original order
                this.delivery.products = this.originalProductsOrder;
                this.reorderMode = false;
                this.originalProductsOrder = null;
            }
        },
        moveUp(index) {
            if (index > 0) {
                const products = [...this.delivery.products];
                const temp = products[index];
                products[index] = products[index - 1];
                products[index - 1] = temp;
                this.delivery.products = products;
            }
        },
        moveDown(index) {
            if (index < this.delivery.products.length - 1) {
                const products = [...this.delivery.products];
                const temp = products[index];
                products[index] = products[index + 1];
                products[index + 1] = temp;
                this.delivery.products = products;
            }
        },
        async saveOrder() {
            try {
                this.loading = true;
                
                const products = this.delivery.products.map(p => {
                    return {
                        id: p.id,
                        inventory_id: p.inventory_id,
                        product_id: p.product_id,
                        product_label: p.product_label,
                        quantity: p.quantity,
                        price: p.price,
                        unity: p.unity,
                        purchase_price: p.purchase_price,
                        vat: p.vat,
                        total: p.total,
                        sub_total: p.sub_total,
                        custom_product: p.custom_product,
                        discount: p.discount,
                        position: p.position
                    }
                })

                await helper.customPatch(`${process.env.VUE_APP_API}/deliveries/${this.delivery.id}`, {
                    reference: this.delivery.reference,
                    date: this.delivery.date,
                    client_id: this.delivery.client_id,
                    order_id: this.delivery.order_id,
                    notes: this.delivery.notes,
                    status: this.delivery.status,
                    products: products
                });

                this.loading = false;
                this.reorderMode = false;
                this.originalProductsOrder = null;
                
                this.$toast.open({
                    message: this.$t('updated_successfully'),
                    type: 'success',
                    position: 'bottom'
                });
                
                // Refresh data
                this.getDelivery(this.$route.params.id);
                
            } catch (error) {
                this.loading = false;
                this.$toast.open({
                    message: error.response?.data?.message || this.$t('error_occurred'),
                    type: 'error',
                    position: 'bottom'
                });
            }
        }
  },
  computed : {
    business(){ return this.$store.getters.BUSINESS},
    helper(){ return helper},
    total(){ return this.delivery.products.length ? this.delivery.products.map(o => o.total).reduce((a, c) => { return a + c }) : 0},
    sub_total(){ return this.delivery.products.length ? this.delivery.products.map(o => o.sub_total).reduce((a, c) => { return a + c }) : 0}
  },
  watch : {
    editModalisOpen(newValue) {
        if(newValue === false){
            this.getDelivery(this.$route.params.id)
        }
    },
    step(newValue) {
        if(newValue === 'history' && this.delivery.id){
            this.getDeliveryHistory(this.delivery.id)
        }
    }
  }
}
</script>

<style scoped>
.product-rich-text::v-deep > :last-child {
    margin-bottom: 0 !important;
}
</style>
